import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [revenue, setRevenue] = useState(0);
  const [count, setCount] = useState(0);
  const [fetched, setFetched] = useState(false);
  const [shipping, setShipping] = useState(null);
  const [filter, setFilter] = useState("all"); // all | unshipped | shipped

  useEffect(() => {
    api.get("/admin/orders").then(({ data }) => {
      setOrders(data.orders);
      setRevenue(data.revenue);
      setCount(data.count);
    }).catch(() => toast.error("Could not load orders")).finally(() => setFetched(true));
  }, []);

  const markShipped = async (o) => {
    if (o.customer_email == null) return toast.error("No customer email on this order.");
    const tracking = window.prompt(
      `Mark shipped and email ${o.customer_email}?\n\nPaste the tracking NUMBER — we'll auto-detect the carrier (UPS/USPS/FedEx/DHL) and add a "Track package" button. You can also paste a full https URL. Leave blank to skip.`,
      o.tracking || ""
    );
    if (tracking === null) return; // cancelled
    setShipping(o.session_id);
    try {
      const { data } = await api.post(`/admin/orders/${o.session_id}/ship`, { tracking: tracking.trim() });
      setOrders((prev) => prev.map((x) => x.session_id === o.session_id
        ? { ...x, fulfillment_status: "shipped", tracking: data.tracking, tracking_url: data.tracking_url, carrier: data.carrier } : x));
      if (tracking.trim() && !data.tracking_url) {
        toast.warning(`Shipped email sent to ${o.customer_email} — couldn't auto-detect the carrier, so no track link was added.`);
      } else {
        toast.success(`Shipped email sent to ${o.customer_email}${data.carrier ? ` · ${data.carrier} link added` : ""}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not mark shipped");
    } finally {
      setShipping(null);
    }
  };

  const isShipped = (o) => o.fulfillment_status === "shipped";
  const unshippedCount = orders.filter((o) => !isShipped(o)).length;
  const visible = orders.filter((o) =>
    filter === "all" ? true : filter === "shipped" ? isShipped(o) : !isShipped(o)
  );
  const filterCls = (f) =>
    `px-3 py-1.5 text-xs uppercase tracking-widest border-2 transition-colors ${
      filter === f ? "bg-[#DFFF00] text-[#090909] border-[#DFFF00]" : "border-[#F2EFEB] hover:bg-[#F2EFEB]/10"
    }`;

  return (
    <div data-testid="orders-panel">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <span data-testid="orders-count" className="border-2 border-[#DFFF00] text-[#DFFF00] px-4 py-2 text-sm">{count} paid orders</span>
        <span data-testid="orders-revenue" className="border-2 border-[#F2EFEB] px-4 py-2 text-sm">{money(revenue)} revenue</span>
        <span data-testid="orders-unshipped-count" className="border-2 border-[#FF2A00] text-[#FF2A00] px-4 py-2 text-sm">{unshippedCount} to ship</span>
      </div>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setFilter("all")} data-testid="orders-filter-all" className={filterCls("all")}>All ({orders.length})</button>
        <button onClick={() => setFilter("unshipped")} data-testid="orders-filter-unshipped" className={filterCls("unshipped")}>Unshipped ({unshippedCount})</button>
        <button onClick={() => setFilter("shipped")} data-testid="orders-filter-shipped" className={filterCls("shipped")}>Shipped ({orders.length - unshippedCount})</button>
      </div>
      <div className="overflow-x-auto no-scrollbar">
        {!fetched ? (
          <p data-testid="orders-loading" className="opacity-60 py-12 text-center animate-pulse">Loading orders...</p>
        ) : orders.length === 0 ? (
          <p data-testid="orders-empty" className="opacity-60 py-12 text-center">No paid orders yet. Flip a product to live and make the first sale.</p>
        ) : visible.length === 0 ? (
          <p data-testid="orders-filter-empty" className="opacity-60 py-12 text-center">
            {filter === "unshipped" ? "All caught up — nothing left to ship." : "No orders match this filter."}
          </p>
        ) : (
          <table className="w-full text-left text-sm border-collapse" data-testid="orders-table">
            <thead>
              <tr className="border-b-2 border-[#F2EFEB] text-[#DFFF00] uppercase text-xs tracking-widest">
                <th className="py-3 pr-4">#</th>
                <th className="py-3 pr-4">Items</th>
                <th className="py-3 pr-4">Total</th>
                <th className="py-3 pr-4">Email</th>
                <th className="py-3 pr-4">Paid</th>
                <th className="py-3 pr-4">Fulfillment</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o, i) => (
                <tr key={o.session_id} className="border-b border-[#F2EFEB]/20 hover:bg-[#F2EFEB]/5" data-testid={`order-row-${i}`}>
                  <td className="py-3 pr-4 opacity-50">{String(i + 1).padStart(3, "0")}</td>
                  <td className="py-3 pr-4">
                    {(o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(", ")}
                  </td>
                  <td className="py-3 pr-4">{money(o.amount)}</td>
                  <td className="py-3 pr-4 opacity-70">{o.customer_email || "—"}</td>
                  <td className="py-3 pr-4 opacity-70">{o.updated_at ? new Date(o.updated_at).toLocaleString() : "—"}</td>
                  <td className="py-3 pr-4">
                    {o.fulfillment_status === "shipped" ? (
                      <span data-testid={`order-shipped-${i}`} className="inline-flex flex-col gap-0.5">
                        <span className="text-[#DFFF00] uppercase text-xs tracking-widest">Shipped ✓</span>
                        {o.tracking && <span className="opacity-50 text-xs">{o.carrier ? `${o.carrier} · ` : ""}{o.tracking}</span>}
                        {o.tracking_url && (
                          <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`order-track-link-${i}`} className="text-xs text-[#DFFF00] underline underline-offset-2 hover:opacity-70">Track ↗</a>
                        )}
                        <button onClick={() => markShipped(o)} disabled={shipping === o.session_id} data-testid={`order-resend-ship-${i}`} className="text-[10px] uppercase opacity-50 hover:opacity-100 hover:text-[#DFFF00] text-left">Resend / update</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => markShipped(o)}
                        disabled={shipping === o.session_id}
                        data-testid={`order-ship-btn-${i}`}
                        className="border-2 border-[#F2EFEB] px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-[#DFFF00] hover:text-[#090909] hover:border-[#DFFF00] transition-colors disabled:opacity-50"
                      >
                        {shipping === o.session_id ? "..." : "Mark Shipped"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
